import React, { useEffect, useState } from "react";
import { Plus, Search, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import ContactCard from "@/components/ContactCard";
import { CONTACT_ROLES } from "@/lib/utils-app";
import { toast } from "sonner";

function NewContactModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "sökande", company: "" });
  const [loading, setLoading] = useState(false);
  if (!open) return null;
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/crm/contacts", form);
      onCreated(data); onClose();
      toast.success("Kontakt skapad");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fel");
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-sm" data-testid="new-contact-modal">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>Ny kontakt</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required value={form.name} onChange={f("name")} placeholder="Namn" data-testid="contact-name"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          <input required type="email" value={form.email} onChange={f("email")} placeholder="E-post" data-testid="contact-email"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          <input value={form.phone} onChange={f("phone")} placeholder="Telefon" data-testid="contact-phone"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          <input value={form.company} onChange={f("company")} placeholder="Företag (valfritt)" data-testid="contact-company"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          <select value={form.role} onChange={f("role")} data-testid="contact-role"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm bg-white">
            {CONTACT_ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-sm">Avbryt</button>
            <button type="submit" data-testid="submit-contact" disabled={loading}
              className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Skapa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [modal, setModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const opts = {};
      if (q) opts.q = q;
      if (role !== "all") opts.role = role;
      const { data } = await api.get("/crm/contacts", { params: opts });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Kontaktregister</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Kontakter
          </h1>
        </div>
        <button onClick={() => setModal(true)} data-testid="open-new-contact"
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-sm text-sm font-medium active:scale-[0.98]">
          <Plus className="w-4 h-4" /> Ny kontakt
        </button>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            data-testid="search-contacts"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Sök kontakter..."
            className="w-full pl-10 pr-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white"
          />
        </div>
        <button onClick={load} className="px-4 py-2.5 border border-zinc-300 rounded-sm text-sm font-medium hover:bg-zinc-100 bg-white">Sök</button>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        {[{ k: "all", label: "Alla" }, ...CONTACT_ROLES.map((r) => ({ k: r, label: r }))].map((f) => (
          <button
            key={f.k}
            data-testid={`role-${f.k}`}
            onClick={() => setRole(f.k)}
            className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-sm border transition ${
              role === f.k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm py-16 text-center text-sm text-zinc-500">Inga kontakter.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((c) => <ContactCard key={c.contact_id} contact={c} />)}
          </div>
        )}
      </div>

      <NewContactModal open={modal} onClose={() => setModal(false)} onCreated={(c) => setItems([c, ...items])} />
    </div>
  );
}
